import { useAutoneTranslation } from '@autone/translations';
import {
  AutoneTooltip,
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellTitle,
  DataTableHeaderCellWithSorting,
  DataTableIntegerValue,
  type DataTableLoadingObject,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';
import { Info } from 'lucide-react';

import { type APP_NAME } from '../../../../consts';

const COLUMN_ID = 'warehouse_units' as const;

export type WarehouseUnitsColumnId = typeof COLUMN_ID;

interface WarehouseUnitsFields {
  warehouse_inventory_units: number;
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<WarehouseUnitsFields>();

export const createWarehouseUnitsColumn = <
  T extends WarehouseUnitsFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.warehouse_inventory_units),
    {
      id: COLUMN_ID,
      size: 175,
      header: function WarehouseUnitsHeader({ column }) {
        const { t } = useAutoneTranslation<typeof APP_NAME>();

        return (
          <DataTableHeaderCellWithSorting
            column={column}
            arrowPosition="left"
            showSortable
          >
            <DataTableCellContent align={'right'}>
              <DataTableCellTitle className="flex flex-row gap-2 items-center uppercase font-medium whitespace-normal text-right">
                {t('table-columns.warehouse-units')}
                <AutoneTooltip
                  text={t(
                    'table-column-tooltips.warehouse-units.product-view-table',
                  )}
                >
                  <Info className="inline" size={14} />
                </AutoneTooltip>
              </DataTableCellTitle>
            </DataTableCellContent>
          </DataTableHeaderCellWithSorting>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function WarehouseUnitsCell({ getValue }) {
            return (
              <DataTableCellContent align="right">
                <DataTableCellTitle>
                  <DataTableIntegerValue value={getValue()} />
                </DataTableCellTitle>
              </DataTableCellContent>
            );
          },
        }),
    },
  );
